import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SignupStepProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  canGoNext: boolean;
  canGoBack: boolean;
  isLoading?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  onNext: () => void;
  onBack: () => void;
  className?: string;
}

export const SignupStep: React.FC<SignupStepProps> = ({
  title,
  description,
  children,
  canGoNext,
  canGoBack,
  isLoading = false,
  isFirstStep = false,
  isLastStep = false,
  nextButtonText,
  backButtonText = "Back",
  onNext,
  onBack,
  className,
}) => {
  const defaultNextText = isLastStep ? "Submit Application" : "Continue";
  const finalNextText = nextButtonText || defaultNextText;

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canGoNext && !isLoading) {
      e.preventDefault();
      onNext();
    }
  };

  return (
    <Card
      className={cn(
        "w-full max-w-2xl mx-auto bg-white dark:bg-white text-gray-900 dark:text-gray-900",
        className
      )}
      onKeyDown={handleKeyPress}
    >
      <CardHeader className="text-center pb-4 bg-white dark:bg-white">
        <CardTitle className="text-2xl font-semibold text-gray-900 dark:text-gray-900">
          {title}
        </CardTitle>
        {description && (
          <CardDescription className="text-base mt-2 text-gray-600 dark:text-gray-600">
            {description}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="space-y-6 bg-white dark:bg-white">
        {/* Step Content */}
        <div className="space-y-4">{children}</div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          {/* Back Button */}
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={!canGoBack || isLoading}
            className={cn("flex items-center gap-2", {
              invisible: isFirstStep,
            })}
          >
            <ArrowLeft className="w-4 h-4" />
            {backButtonText}
          </Button>

          {/* Next/Submit Button */}
          <Button
            type="button"
            onClick={onNext}
            disabled={!canGoNext || isLoading}
            className="flex items-center gap-2 min-w-[120px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {finalNextText}
                {!isLastStep && <ArrowRight className="w-4 h-4" />}
              </>
            )}
          </Button>
        </div>

        {/* Auto-save indicator */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Your progress is automatically saved</p>
        </div>
      </CardContent>
    </Card>
  );
};
